import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { RotateCcw, HelpCircle, Copy, LogOut, Plus, Coins, Users, Send, MessageSquare, UserPlus, User } from "lucide-react";
import { API, WS_URL, AuthContext } from "../App.js";
import axios from "axios";

// Compact 8-Ball Pool with Practice Mode and Multiplayer
const PoolGame = ({ currentUser, sendGameMessage }) => {
  const { user, token } = useContext(AuthContext);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Mode: 'lobby', 'practice', 'game'
  const [view, setView] = useState('lobby');
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [tables, setTables] = useState([]);
  const [currentTable, setCurrentTable] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userFChips, setUserFChips] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [friends, setFriends] = useState([]);
  const [showChat, setShowChat] = useState(true);
  const [showFriends, setShowFriends] = useState(true);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);
  const [isHost, setIsHost] = useState(false);
  
  // Game state
  const [gameState, setGameState] = useState("aiming");
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [playerType, setPlayerType] = useState(null);
  const [pocketedBalls, setPocketedBalls] = useState({ solids: [], stripes: [] });
  const [winner, setWinner] = useState(null);
  const [message, setMessage] = useState("Your shot!");
  const [showHelp, setShowHelp] = useState(false);
  const [shotsTaken, setShotsTaken] = useState(0);
  
  // Cue control
  const [isDragging, setIsDragging] = useState(false);
  const [cueAngle, setCueAngle] = useState(0);
  const [cuePullBack, setCuePullBack] = useState(0);
  const lockedAngleRef = useRef(null); // Lock angle when pulling back
  
  // Table dimensions - Larger for better visibility
  const TABLE_WIDTH = 600;
  const TABLE_HEIGHT = 300;
  const BALL_RADIUS = 9;
  const POCKET_RADIUS = 18;
  const FRICTION = 0.985;
  const MIN_VELOCITY = 0.1;
  const MAX_POWER = 18;
  const CUSHION = 15;
  
  // Ball colors
  const BALL_COLORS = {
    0: { color: "#FFFFFF", stripe: false, number: null },
    1: { color: "#FFD700", stripe: false, number: 1 },
    2: { color: "#0000FF", stripe: false, number: 2 },
    3: { color: "#FF0000", stripe: false, number: 3 },
    4: { color: "#800080", stripe: false, number: 4 },
    5: { color: "#FFA500", stripe: false, number: 5 },
    6: { color: "#008000", stripe: false, number: 6 },
    7: { color: "#8B0000", stripe: false, number: 7 },
    8: { color: "#000000", stripe: false, number: 8 },
    9: { color: "#FFD700", stripe: true, number: 9 },
    10: { color: "#0000FF", stripe: true, number: 10 },
    11: { color: "#FF0000", stripe: true, number: 11 },
    12: { color: "#800080", stripe: true, number: 12 },
    13: { color: "#FFA500", stripe: true, number: 13 },
    14: { color: "#008000", stripe: true, number: 14 },
    15: { color: "#8B0000", stripe: true, number: 15 },
  };
  
  // Pockets
  const POCKETS = [
    { x: CUSHION + 2, y: CUSHION + 2 },
    { x: TABLE_WIDTH / 2, y: CUSHION - 1 },
    { x: TABLE_WIDTH - CUSHION - 2, y: CUSHION + 2 },
    { x: CUSHION + 2, y: TABLE_HEIGHT - CUSHION - 2 },
    { x: TABLE_WIDTH / 2, y: TABLE_HEIGHT - CUSHION + 1 },
    { x: TABLE_WIDTH - CUSHION - 2, y: TABLE_HEIGHT - CUSHION - 2 },
  ];
  
  // Ball state refs
  const ballsRef = useRef([]);
  const cueBallRef = useRef({ x: 100, y: TABLE_HEIGHT / 2, vx: 0, vy: 0, pocketed: false });
  const pocketedThisTurnRef = useRef([]);
  const foulRef = useRef(false);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [balanceRes, friendsRes] = await Promise.all([
          axios.get(`${API}/fchips/balance`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/friends`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setUserFChips(balanceRes.data.balance);
        // The /friends endpoint returns an array directly, not { friends: [] }
        setFriends(Array.isArray(friendsRes.data) ? friendsRes.data : friendsRes.data.friends || []);
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, [token]);

  // Initialize balls in rack formation
  const initializeBalls = useCallback(() => {
    const startX = TABLE_WIDTH * 0.7;
    const startY = TABLE_HEIGHT / 2;
    const spacing = BALL_RADIUS * 2.1;
    const rackOrder = [1, 9, 2, 10, 8, 11, 3, 12, 4, 13, 5, 14, 6, 15, 7];
    
    const balls = [];
    let idx = 0;
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col <= row; col++) {
        if (idx < rackOrder.length) {
          const num = rackOrder[idx];
          balls.push({
            id: num,
            x: startX + row * spacing * 0.866,
            y: startY + (col - row / 2) * spacing,
            vx: 0, vy: 0,
            pocketed: false,
            ...BALL_COLORS[num]
          });
          idx++;
        }
      }
    }
    ballsRef.current = balls;
    cueBallRef.current = { x: 100, y: TABLE_HEIGHT / 2, vx: 0, vy: 0, pocketed: false };
    setPocketedBalls({ solids: [], stripes: [] });
    setPlayerType(null);
    setWinner(null);
    setShotsTaken(0);
  }, []);
  
  // Physics helpers
  const checkBallCollision = (b1, b2) => {
    const dx = b2.x - b1.x;
    const dy = b2.y - b1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist < BALL_RADIUS * 2 && dist > 0) {
      const nx = dx / dist, ny = dy / dist;
      const p = 2 * (b1.vx * nx + b1.vy * ny - b2.vx * nx - b2.vy * ny) / 2;
      b1.vx -= p * nx; b1.vy -= p * ny;
      b2.vx += p * nx; b2.vy += p * ny;
      const overlap = BALL_RADIUS * 2 - dist;
      b1.x -= overlap / 2 * nx; b1.y -= overlap / 2 * ny;
      b2.x += overlap / 2 * nx; b2.y += overlap / 2 * ny;
      return true;
    }
    return false;
  };
  
  const checkPocket = (ball) => {
    for (const p of POCKETS) {
      const dx = ball.x - p.x, dy = ball.y - p.y;
      if (Math.sqrt(dx * dx + dy * dy) < POCKET_RADIUS) return true;
    }
    return false;
  };
  
  const handleWall = (ball) => {
    if (ball.x - BALL_RADIUS < CUSHION) { ball.x = CUSHION + BALL_RADIUS; ball.vx = -ball.vx * 0.8; }
    if (ball.x + BALL_RADIUS > TABLE_WIDTH - CUSHION) { ball.x = TABLE_WIDTH - CUSHION - BALL_RADIUS; ball.vx = -ball.vx * 0.8; }
    if (ball.y - BALL_RADIUS < CUSHION) { ball.y = CUSHION + BALL_RADIUS; ball.vy = -ball.vy * 0.8; }
    if (ball.y + BALL_RADIUS > TABLE_HEIGHT - CUSHION) { ball.y = TABLE_HEIGHT - CUSHION - BALL_RADIUS; ball.vy = -ball.vy * 0.8; }
  };
  
  // Game loop
  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    
    // Draw table felt
    ctx.fillStyle = "#1a5f2a";
    ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    
    // Cushions
    ctx.fillStyle = "#0d3d18";
    ctx.fillRect(0, 0, TABLE_WIDTH, CUSHION);
    ctx.fillRect(0, TABLE_HEIGHT - CUSHION, TABLE_WIDTH, CUSHION);
    ctx.fillRect(0, 0, CUSHION, TABLE_HEIGHT);
    ctx.fillRect(TABLE_WIDTH - CUSHION, 0, CUSHION, TABLE_HEIGHT);
    
    // Rail
    ctx.strokeStyle = "#5d3a1a";
    ctx.lineWidth = 3;
    ctx.strokeRect(1, 1, TABLE_WIDTH - 2, TABLE_HEIGHT - 2);
    
    // Pockets
    POCKETS.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, POCKET_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = "#111";
      ctx.fill();
    });
    
    // Update physics
    let moving = false;
    const cue = cueBallRef.current;
    
    if (!cue.pocketed) {
      cue.x += cue.vx; cue.y += cue.vy;
      cue.vx *= FRICTION; cue.vy *= FRICTION;
      if (Math.abs(cue.vx) < MIN_VELOCITY) cue.vx = 0;
      if (Math.abs(cue.vy) < MIN_VELOCITY) cue.vy = 0;
      if (Math.abs(cue.vx) > 0 || Math.abs(cue.vy) > 0) moving = true;
      handleWall(cue);
      if (checkPocket(cue)) { 
        cue.pocketed = true; 
        foulRef.current = true; 
        setMessage("Scratch! Ball in hand."); 
      }
    }
    
    ballsRef.current.forEach(ball => {
      if (ball.pocketed) return;
      ball.x += ball.vx; ball.y += ball.vy;
      ball.vx *= FRICTION; ball.vy *= FRICTION;
      if (Math.abs(ball.vx) < MIN_VELOCITY) ball.vx = 0;
      if (Math.abs(ball.vy) < MIN_VELOCITY) ball.vy = 0;
      if (Math.abs(ball.vx) > 0 || Math.abs(ball.vy) > 0) moving = true;
      handleWall(ball);
      if (checkPocket(ball)) { ball.pocketed = true; pocketedThisTurnRef.current.push(ball); }
      if (!cue.pocketed) checkBallCollision(cue, ball);
    });
    
    // Ball-ball collisions
    for (let i = 0; i < ballsRef.current.length; i++) {
      for (let j = i + 1; j < ballsRef.current.length; j++) {
        if (!ballsRef.current[i].pocketed && !ballsRef.current[j].pocketed) {
          checkBallCollision(ballsRef.current[i], ballsRef.current[j]);
        }
      }
    }
    
    // Draw balls
    ballsRef.current.forEach(ball => {
      if (ball.pocketed) return;
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, BALL_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = ball.color;
      ctx.fill();
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 0.5;
      ctx.stroke();
      
      if (ball.stripe) {
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, BALL_RADIUS * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = "white";
        ctx.fill();
      }
      
      if (ball.number) {
        ctx.fillStyle = ball.stripe ? ball.color : "white";
        ctx.font = "bold 5px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(ball.number, ball.x, ball.y);
      }
    });
    
    // Draw cue ball
    if (!cue.pocketed) {
      ctx.beginPath();
      ctx.arc(cue.x, cue.y, BALL_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.strokeStyle = "#ccc";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
    
    // Draw cue stick when aiming
    const canShoot = isPracticeMode || currentPlayer === myPlayerId;
    if ((gameState === "aiming" || isDragging) && !cue.pocketed && canShoot) {
      const cueLength = 120;
      const startX = cue.x - Math.cos(cueAngle) * (BALL_RADIUS + 3 + cuePullBack);
      const startY = cue.y - Math.sin(cueAngle) * (BALL_RADIUS + 3 + cuePullBack);
      const endX = startX - Math.cos(cueAngle) * cueLength;
      const endY = startY - Math.sin(cueAngle) * cueLength;
      
      // Trajectory line
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = "rgba(255,255,255,0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cue.x, cue.y);
      ctx.lineTo(cue.x + Math.cos(cueAngle) * 80, cue.y + Math.sin(cueAngle) * 80);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Cue stick
      ctx.strokeStyle = "#8B4513";
      ctx.lineWidth = 5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      
      // Tip
      ctx.strokeStyle = "#F5DEB3";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(startX - Math.cos(cueAngle) * 8, startY - Math.sin(cueAngle) * 8);
      ctx.stroke();
      
      // Power bar
      if (cuePullBack > 0) {
        const pct = Math.min(cuePullBack / 60, 1);
        ctx.fillStyle = `rgba(255, ${255 - pct * 200}, 0, 0.8)`;
        ctx.fillRect(10, TABLE_HEIGHT - 16, pct * 80, 8);
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1;
        ctx.strokeRect(10, TABLE_HEIGHT - 16, 80, 8);
      }
    }
    
    // Turn end check
    if (!moving && gameState === "moving") handleTurnEnd();
    
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, isDragging, cueAngle, cuePullBack, currentPlayer, myPlayerId, isPracticeMode]);
  
  // Handle turn end
  const handleTurnEnd = () => {
    const pocketed = pocketedThisTurnRef.current;
    
    // Process pocketed balls
    pocketed.forEach(ball => {
      if (ball.id === 8) {
        // 8-ball pocketed
        const allMyBallsPocketed = playerType === "solid" 
          ? pocketedBalls.solids.length === 7 
          : pocketedBalls.stripes.length === 7;
        
        if (allMyBallsPocketed && !foulRef.current) {
          setWinner("You");
          setGameState("gameover");
          setMessage("🎉 You Win!");
        } else {
          setWinner("Opponent");
          setGameState("gameover");
          setMessage("8-ball sunk too early! Game Over.");
        }
        return;
      }
      
      const isSolid = ball.id <= 7;
      
      // Assign type on first pocket
      if (!playerType) {
        setPlayerType(isSolid ? "solid" : "stripe");
        setMessage(isSolid ? "You're SOLIDS (1-7)" : "You're STRIPES (9-15)");
      }
      
      // Add to pocketed list
      if (isSolid) {
        setPocketedBalls(prev => ({ ...prev, solids: [...prev.solids, ball.id] }));
      } else {
        setPocketedBalls(prev => ({ ...prev, stripes: [...prev.stripes, ball.id] }));
      }
    });
    
    // Reset cue ball if scratched
    if (cueBallRef.current.pocketed) {
      cueBallRef.current = { x: 100, y: TABLE_HEIGHT / 2, vx: 0, vy: 0, pocketed: false };
    }
    
    // Reset for next shot
    pocketedThisTurnRef.current = [];
    foulRef.current = false;
    setCuePullBack(0);
    
    if (gameState !== "gameover") {
      setGameState("aiming");
      if (isPracticeMode) {
        setMessage("Your shot!");
      }
    }
  };
  
  // Mouse handlers
  const handleMouseDown = (e) => {
    if (gameState !== "aiming") return;
    if (!isPracticeMode && currentPlayer !== myPlayerId) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (TABLE_WIDTH / rect.width);
    const y = (e.clientY - rect.top) * (TABLE_HEIGHT / rect.height);
    const cue = cueBallRef.current;
    const dx = x - cue.x, dy = y - cue.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist > BALL_RADIUS && dist < 200) {
      setIsDragging(true);
      // Lock the angle when starting to pull back
      lockedAngleRef.current = Math.atan2(dy, dx);
      setCueAngle(lockedAngleRef.current);
    }
  };
  
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (TABLE_WIDTH / rect.width);
    const y = (e.clientY - rect.top) * (TABLE_HEIGHT / rect.height);
    const cue = cueBallRef.current;
    
    if (isDragging) {
      // When dragging/pulling back, ONLY calculate power - angle is LOCKED
      const dx = cue.x - x, dy = cue.y - y;
      // Use the locked angle to calculate pull back distance along that direction
      const lockAngle = lockedAngleRef.current;
      if (lockAngle !== null) {
        // Project the drag onto the locked angle direction
        const dragDist = Math.sqrt(dx * dx + dy * dy);
        // Check if pulling back (away from aim direction)
        const dragAngle = Math.atan2(dy, dx);
        const angleDiff = Math.abs(dragAngle - (lockAngle + Math.PI));
        // Only register pullback if dragging roughly opposite to aim direction
        const pullBack = dragAngle ? dragDist - BALL_RADIUS : 0;
        setCuePullBack(Math.max(0, Math.min(pullBack, 60)));
      }
    } else if (gameState === "aiming" && (isPracticeMode || currentPlayer === myPlayerId)) {
      // Not dragging - freely aim the cue
      const dx = x - cue.x, dy = y - cue.y;
      setCueAngle(Math.atan2(dy, dx));
    }
  };
  
  const handleMouseUp = () => {
    if (isDragging && cuePullBack > 3) {
      // Use the LOCKED angle for the shot, not current mouse position
      const shootAngle = lockedAngleRef.current !== null ? lockedAngleRef.current : cueAngle;
      const power = (cuePullBack / 60) * MAX_POWER;
      const cue = cueBallRef.current;
      cue.vx = Math.cos(shootAngle) * power;
      cue.vy = Math.sin(shootAngle) * power;
      setGameState("moving");
      setMessage("...");
      setShotsTaken(prev => prev + 1);
      
      // Broadcast shot to opponent via WebSocket
      broadcastShot(shootAngle, power);
    }
    setIsDragging(false);
    setCuePullBack(0);
    lockedAngleRef.current = null; // Reset locked angle
  };
  
  // Start practice mode
  const startPractice = () => {
    setIsPracticeMode(true);
    setView('game');
    initializeBalls();
    setGameState("aiming");
    setMessage("Practice Mode - Take your shot!");
    setMyPlayerId("practice");
    setCurrentPlayer("practice");
  };
  
  // Start game loop
  useEffect(() => {
    if ((view === 'game' || view === 'practice') && canvasRef.current) {
      if (!isPracticeMode || ballsRef.current.length === 0) {
        initializeBalls();
      }
      animationRef.current = requestAnimationFrame(gameLoop);
      return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
    }
  }, [view, gameLoop, initializeBalls, isPracticeMode]);
  
  // Reset game
  const resetGame = () => {
    initializeBalls();
    setGameState("aiming");
    setMessage(isPracticeMode ? "Practice Mode - Take your shot!" : "Your shot!");
  };
  
  // WebSocket connection for real-time multiplayer
  const connectWebSocket = useCallback((tableCode, playerId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = `${WS_URL}/pool/${tableCode}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('Pool WebSocket connected');
      ws.send(JSON.stringify({
        type: 'join',
        token: token,
        player_id: playerId
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connected':
            setPlayers(data.players || []);
            if (data.game_state?.balls) {
              setBalls(data.game_state.balls);
            }
            break;
            
          case 'player_joined':
            setPlayers(data.players || []);
            setMessage('Opponent joined! Break!');
            if (isHost) {
              setGameState('aiming');
              setCurrentPlayer(myPlayerId);
            }
            break;
            
          case 'player_left':
          case 'player_disconnected':
            setMessage(`${data.player || 'Opponent'} left the table`);
            setGameState('waiting');
            setPlayers(prev => prev.filter(p => p.name !== data.player));
            break;
            
          case 'game_state':
            // Sync game state from host
            if (data.balls) setBalls(data.balls);
            if (data.current_player !== undefined) setCurrentPlayer(data.current_player);
            if (data.player_types) setPlayerType(data.player_types[myPlayerId] || null);
            if (data.pocketed) setPocketedBalls(data.pocketed);
            if (data.winner) {
              setWinner(data.winner);
              setGameState('gameOver');
            }
            break;
            
          case 'shot':
            // Opponent took a shot - animate it
            if (!isHost) {
              // Apply the shot to cue ball
              if (cueBallRef.current && data.angle !== undefined && data.power !== undefined) {
                const shotPower = data.power;
                cueBallRef.current.vx = Math.cos(data.angle) * shotPower;
                cueBallRef.current.vy = Math.sin(data.angle) * shotPower;
                setGameState('moving');
                setMessage(`${data.player} shoots!`);
              }
            }
            break;
            
          case 'turn_change':
            setCurrentPlayer(data.current_player);
            const isMyTurn = players[data.current_player]?.odyn === myPlayerId;
            setMessage(isMyTurn ? "Your turn!" : "Opponent's turn");
            setGameState('aiming');
            break;
            
          case 'game_over':
            setWinner(data.winner);
            setGameState('gameOver');
            setMessage(data.winner === user?.username ? '🎉 You win!' : `${data.winner} wins!`);
            break;
            
          case 'chat':
            setChatMessages(prev => [...prev.slice(-50), {
              id: Date.now(),
              player: data.player,
              message: data.message
            }]);
            break;
            
          case 'error':
            setError(data.message);
            break;
        }
      } catch (e) {
        console.error('Pool WS parse error:', e);
      }
    };
    
    ws.onerror = (err) => {
      console.error('Pool WebSocket error:', err);
    };
    
    ws.onclose = () => {
      console.log('Pool WebSocket closed');
    };
    
    wsRef.current = ws;
  }, [token, myPlayerId, isHost, players, user?.username]);
  
  // Send game state to opponent (host only)
  const broadcastGameState = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && isHost && !isPracticeMode) {
      wsRef.current.send(JSON.stringify({
        type: 'game_state',
        balls: ballsRef.current,
        cue_ball: cueBallRef.current,
        current_player: currentPlayer,
        game_started: true,
        player_types: { [myPlayerId]: playerType },
        pocketed: pocketedBalls,
        winner: winner
      }));
    }
  }, [currentPlayer, isHost, isPracticeMode, myPlayerId, playerType, pocketedBalls, winner]);
  
  // Send shot to opponent
  const broadcastShot = useCallback((angle, power) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && !isPracticeMode) {
      wsRef.current.send(JSON.stringify({
        type: 'shot',
        angle: angle,
        power: power,
        cue_ball: cueBallRef.current
      }));
    }
  }, [isPracticeMode]);
  
  // Send chat message
  const sendChatMessage = useCallback(() => {
    if (!chatInput.trim()) return;
    
    if (wsRef.current?.readyState === WebSocket.OPEN && !isPracticeMode) {
      wsRef.current.send(JSON.stringify({
        type: 'chat',
        message: chatInput.trim()
      }));
    } else {
      // Practice mode - just add locally
      setChatMessages(prev => [...prev.slice(-50), {
        id: Date.now(),
        player: user?.username,
        message: chatInput.trim()
      }]);
    }
    setChatInput('');
  }, [chatInput, isPracticeMode, user?.username]);
  
  // Broadcast state periodically when balls are moving (host only)
  useEffect(() => {
    if (isHost && gameState === 'shooting' && !isPracticeMode) {
      const interval = setInterval(broadcastGameState, 100);
      return () => clearInterval(interval);
    }
  }, [isHost, gameState, isPracticeMode, broadcastGameState]);
  
  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Fetch tables
  const fetchTables = async () => {
    try {
      const res = await axios.get(`${API}/pool/tables`, { headers: { Authorization: `Bearer ${token}` } });
      setTables(res.data.tables || []);
    } catch (err) { setTables([]); }
  };
  
  useEffect(() => {
    if (view === 'lobby') {
      fetchTables();
      const interval = setInterval(fetchTables, 5000);
      return () => clearInterval(interval);
    }
  }, [view]);
  
  // Table actions
  const createTable = async () => {
    setLoading(true);
    setIsPracticeMode(false);
    try {
      const res = await axios.post(`${API}/pool/tables/create`, { name: `${user?.username}'s Table`, bet_amount: 100 }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentTable(res.data.table);
      setMyPlayerId(res.data.player_id);
      setPlayers([{ odyn: res.data.player_id, name: user?.username }]);
      setUserFChips(res.data.new_balance);
      setIsHost(true);
      setView('game');
      setGameState('waiting');
      setMessage('Waiting for opponent...');
      // Connect WebSocket
      connectWebSocket(res.data.table.code, res.data.player_id);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  
  const joinTable = async (code) => {
    const c = (code || joinCode).toUpperCase();
    if (!c) return;
    setLoading(true);
    setIsPracticeMode(false);
    try {
      const res = await axios.post(`${API}/pool/tables/join`, { code: c }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentTable(res.data.table);
      setMyPlayerId(res.data.player_id);
      setPlayers(res.data.players || []);
      setUserFChips(res.data.new_balance);
      setIsHost(false);
      setView('game');
      initializeBalls();
      setGameState('aiming');
      setCurrentPlayer(res.data.players?.[0]?.odyn);
      setMessage(res.data.players?.[0]?.odyn === res.data.player_id ? "Your turn - break!" : "Opponent breaks");
      setJoinCode('');
      // Connect WebSocket
      connectWebSocket(c, res.data.player_id);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  
  const leaveTable = () => {
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (!isPracticeMode && currentTable) {
      axios.post(`${API}/pool/tables/leave`, { code: currentTable?.code }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    setCurrentTable(null);
    setPlayers([]);
    setChatMessages([]);
    setIsPracticeMode(false);
    setIsHost(false);
    setView('lobby');
  };

  // LOBBY VIEW
  if (view === 'lobby') {
    return (
      <div className="h-full bg-gradient-to-b from-[#1a3a2f] to-[#0f2820] p-4 flex flex-col">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-white">🎱 8-BALL POOL</h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Coins className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-bold text-lg">{userFChips.toLocaleString()} F-Chips</span>
          </div>
        </div>

        {error && <div className="bg-red-500/20 border border-red-500 text-red-300 text-sm p-2 rounded mb-3">{error}</div>}

        {/* Practice Mode Button */}
        <button 
          onClick={startPractice}
          className="w-full py-3 mb-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 rounded-lg text-white font-bold flex items-center justify-center gap-2 shadow-lg"
        >
          <User className="w-5 h-5" /> PRACTICE MODE (Solo)
        </button>

        <div className="flex gap-2 mb-4">
          <button onClick={createTable} disabled={loading} className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-lg text-white font-bold flex items-center justify-center gap-2 shadow-lg">
            <Plus className="w-5 h-5" /> Create Table
          </button>
          <div className="flex-1 flex gap-1">
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="CODE"
              className="flex-1 px-3 py-2 rounded-l-lg bg-gray-800 text-white text-center uppercase tracking-widest border border-gray-600"
              maxLength={6}
            />
            <button onClick={() => joinTable()} className="px-4 bg-blue-600 hover:bg-blue-500 rounded-r-lg text-white font-bold">Join</button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <h3 className="text-gray-400 text-sm mb-2 uppercase">Available Tables</h3>
          {tables.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No tables. Create one or practice solo!</div>
          ) : (
            <div className="space-y-2">
              {tables.map(t => (
                <div key={t.code} className="flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 p-3 rounded-lg border border-gray-700">
                  <div>
                    <div className="text-white font-bold">{t.name}</div>
                    <div className="text-gray-400 text-sm">{t.player_count}/2 • Bet: {t.bet_amount}</div>
                  </div>
                  <button onClick={() => joinTable(t.code)} disabled={t.player_count >= 2} className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded text-white font-bold text-sm disabled:opacity-50">
                    {t.player_count >= 2 ? 'Full' : 'JOIN'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="mt-2 text-xs text-gray-500 text-center">Drag cue stick back to set power, release to shoot</div>
      </div>
    );
  }
  
  // GAME VIEW
  return (
    <div className="h-full flex bg-[#1a3a2f]">
      {/* LEFT - Chat (hide in practice) */}
      {!isPracticeMode && showChat && (
        <div className="w-36 bg-gray-900/80 flex flex-col border-r border-gray-700">
          <div className="p-2 border-b border-gray-700 flex items-center justify-between">
            <span className="text-white text-xs font-bold"><MessageSquare className="w-3 h-3 inline mr-1" />Chat</span>
            <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-white text-xs">×</button>
          </div>
          <div className="flex-1 overflow-auto p-1 space-y-0.5 text-[10px]">
            {chatMessages.map((m, i) => (
              <div key={i} className={m.system ? 'text-yellow-400 italic' : ''}>
                {m.system ? m.text : <><span className="text-blue-400">{m.name}:</span> <span className="text-gray-300">{m.text}</span></>}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        </div>
      )}

      {/* CENTER - Pool Table */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center justify-between bg-black/40 px-2 py-1 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-white font-bold">
              {isPracticeMode ? "🎯 Practice Mode" : currentTable?.name}
            </span>
            {!isPracticeMode && currentTable && (
              <button onClick={() => navigator.clipboard.writeText(currentTable?.code)} className="text-gray-400 hover:text-white flex items-center gap-1 text-[10px]">
                <Copy className="w-3 h-3" /> {currentTable?.code}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isPracticeMode && <span className="text-purple-400 text-xs">Shots: {shotsTaken}</span>}
            {!isPracticeMode && <span className="text-yellow-400 text-xs"><Coins className="w-3 h-3 inline mr-1" />{userFChips.toLocaleString()}</span>}
            <button onClick={leaveTable} className="text-red-400 hover:text-red-300 flex items-center gap-1">
              <LogOut className="w-3 h-3" /> {isPracticeMode ? 'Exit' : 'Leave'}
            </button>
          </div>
        </div>

        {/* Game area */}
        <div className="flex-1 flex flex-col items-center justify-center p-2">
          <div className="text-white text-sm font-bold mb-1">{message}</div>
          
          <canvas
            ref={canvasRef}
            width={TABLE_WIDTH}
            height={TABLE_HEIGHT}
            className="border-4 border-amber-900 rounded cursor-crosshair shadow-xl"
            style={{ maxWidth: '100%', height: 'auto' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
          
          {/* Ball count display */}
          <div className="flex justify-between w-full max-w-[600px] mt-2 text-[10px]">
            <div className="p-1.5 rounded bg-yellow-600/50">
              <div className="text-white font-bold">Solids (1-7)</div>
              <div className="text-yellow-200">{pocketedBalls.solids.length}/7 pocketed</div>
            </div>
            <div className="p-1.5 rounded bg-blue-600/50">
              <div className="text-white font-bold">Stripes (9-15)</div>
              <div className="text-blue-200">{pocketedBalls.stripes.length}/7 pocketed</div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="mt-2 flex gap-2">
            <button onClick={resetGame} className="px-3 py-1 bg-green-600 hover:bg-green-500 rounded text-white text-xs flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> {gameState === 'gameover' ? 'New Game' : 'Re-rack'}
            </button>
            <button onClick={() => setShowHelp(!showHelp)} className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white text-xs flex items-center gap-1">
              <HelpCircle className="w-3 h-3" /> Help
            </button>
          </div>
          
          {showHelp && (
            <div className="mt-2 bg-gray-800 p-2 rounded text-[10px] text-gray-300 max-w-[600px]">
              <strong>How to play:</strong> Click near the cue ball and drag BACK to aim and set power. Release to shoot. Pocket all your balls (solids or stripes), then the 8-ball to win!
            </div>
          )}
        </div>
      </div>

      {/* RIGHT - Friends (hide in practice) */}
      {!isPracticeMode && showFriends && (
        <div className="w-32 bg-gray-900/80 flex flex-col border-l border-gray-700">
          <div className="p-2 border-b border-gray-700 flex items-center justify-between">
            <span className="text-white text-xs font-bold"><Users className="w-3 h-3 inline mr-1" />Friends</span>
            <button onClick={() => setShowFriends(false)} className="text-gray-500 hover:text-white text-xs">×</button>
          </div>
          <div className="flex-1 overflow-auto p-1 space-y-0.5">
            {friends.length === 0 ? (
              <div className="text-gray-500 text-[10px] text-center py-4">No friends online</div>
            ) : (
              friends.map((f, i) => (
                <div key={i} className="flex items-center gap-1 p-1 rounded hover:bg-gray-800 cursor-pointer">
                  <div className={`w-1.5 h-1.5 rounded-full ${f.status === 'online' ? 'bg-green-500' : 'bg-gray-500'}`} />
                  <span className="text-gray-300 text-[10px] truncate">{f.username}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PoolGame;
